import React, { ReactNode, useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

const ringFill = keyframes`
  from { stroke-dashoffset: 75.4; }
  to   { stroke-dashoffset: 0; }
`

const TimerDot = styled.div<{ active: boolean }>`
  position: relative;
  width: 30px;
  height: 30px;
  cursor: pointer;
  flex-shrink: 0;

  .dot-inner {
    position: absolute;
    inset: 4px;
    border-radius: 50%;
    background: ${({ active }) => (active ? '#9482a3' : 'transparent')};
    border: 2px solid #9482a3;
    transition: background 0.25s;
  }

  svg {
    position: absolute;
    inset: 0;
    transform: rotate(-90deg);
    overflow: visible;

    circle.ring {
      fill: none;
      stroke: #341644;
      stroke-width: 2.5;
      stroke-linecap: round;
      stroke-dasharray: 75.4;
      stroke-dashoffset: 75.4;
      ${({ active }) => active && css`animation: ${ringFill} 6s linear forwards;`}
    }
  }
`

const Wrapper = styled.div`
color: #341644;
h2 {
  text-align: center;
}
h2, h3 {
  font-weight: bold;
}

padding:  0 80px;

.content {
  margin-top: 30px;
  min-height: 300px;
  position: relative;
  display: flex;
  transform: rotate(-2deg);
  font-size: 1.3em;

  h3 {
    font-size: 1.8em;
  }

  .paper--content {
    z-index: 1;
    max-width: 960px;
    min-height: 35vw;
    padding: 70px;
    padding-top: 30px;
    padding-right: 250px;
    display: flex;
    flex-direction: column;
  }

  .screen--content {
    max-height: 400px;

    flex: 1;
  }

  .image {
    margin-left: -16%;
    margin-top: 5%;
    transform: rotate(10deg);
    height: 100%;
    width: 800px;
    img, .gatsby-image-wrapper {
      width: 100%;
    }
  }
}

.dots {
  display: flex;
  gap: 10px;
}


@media screen and (max-width: 1180px) {
  .content {
    font-size: 1.2em;
    .paper--content {
      padding-top: 40px;
      padding-bottom: 40px;
      padding-right: 200px;
      font-size: 1em;

      .dots {
        margin-top: auto;
      }
    }
  }

  h3 {
    margin-top: 0;
  }
}

@media screen and (max-width: 821px) {
  padding:  0 30px;
  .content {
    flex-direction: column-reverse;

    .paper--content {
      padding-top: 240px;
      padding-bottom: 40px;
      padding-right: 20px;
      padding-left: 80px;
    }
  }

  h2 {
    font-size: 2.5em;
    text-align: left;
  }

  .content {
    font-size: 1em;
  }

  .content .image {
    margin-top: 0%;
    margin-bottom: -30%;
    width: 500px;
    transform: rotate(-10deg) translateX(-60px);
  }
}

@media screen and (max-width: 821px) {
  .content {
    .paper--content {
      padding-left: 60px;
      padding-top: 150px;
    }
  }
`

const Paper = styled.div`
--line-space: 30px;
--line-height: 3px;
--masked-top: 60px;
--masked-bottom: 40px;
height: fit-content;
position: relative;
z-index: 0;
background-color: #f3f0f4;
box-shadow: -2px -2px 8px rgba(0,0,0,0.2);
background-image:
  linear-gradient(#f3f0f4 var(--masked-top), transparent var(--masked-top)),
  linear-gradient(to top, #f3f0f4 var(--masked-bottom), transparent var(--masked-bottom)),
  repeating-linear-gradient(
    to bottom,
    transparent,
    transparent calc(var(--line-space) - var(--line-height)),
    #e5e9ef calc(var(--line-space) - var(--line-height)),
    #e5e9ef var(--line-space)
  );
width: fit-content;
border-radius: 1.4em;
::before {
  position: absolute;
  z-index: -1;
  content: '';
  top: 0;
  left: 50px;
  height: 100%;
  width: 2px;
  background-color: #f0dfdf;
}


`

export const ParentsV2_ProductsHighlights = (props: {
  title: string
  highlights: {
    img: ReactNode
    title: string
    text: string
  }[]
}) => {
  const [screenIndex, setScreenIdx] = useState(0)
  const [autoPlay, setAutoPlay] = useState(true)
  const [inView, setInView] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const screen = props.highlights[screenIndex]

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.3 }
    )
    if (wrapperRef.current) observer.observe(wrapperRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!autoPlay || !inView) return
    const id = setInterval(() => {
      setScreenIdx(i => (i + 1) % props.highlights.length)
    }, 6000)
    return () => clearInterval(id)
  }, [autoPlay, inView, props.highlights.length])

  const handleDotClick = (i: number) => {
    setAutoPlay(false)
    setScreenIdx(i)
  }

  return <Wrapper ref={wrapperRef}>
    <h2>{props.title}</h2>
    <div className='content'>
      <Paper>
        <div className='paper--content'>

          <div className='screen--content'>
            <h3>{screen.title}</h3>
            <p>{screen.text}</p>
          </div>

          <div className='dots'>
            {Array.from(props.highlights).map((_, i) => (
              <TimerDot
                key={i}
                active={screenIndex === i}
                onClick={() => handleDotClick(i)}
              >
                <div className="dot-inner" />
                <svg key={screenIndex === i ? screenIndex : 'idle'} viewBox="0 0 30 30">
                  <circle className="ring" cx="15" cy="15" r="12" />
                </svg>
              </TimerDot>
            ))}
          </div>

        </div>


      </Paper>

      <div className='image'>
        {screen.img}
      </div>
    </div>
  </Wrapper>
}
